import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  MessageCircle, Search, ArrowLeft, Send, Loader2, CircleDashed,
  CheckCircle2, Lock, Filter, ImageIcon, Paperclip, X
} from 'lucide-react';
import { supportApi, contentApi } from '../../lib/api';
import { toast } from 'sonner';

/**
 * Admin Support Panel — list of all tickets with filter, conversation view,
 * reply box, status controls. Mounted as a tab inside AdminPanel.
 */
export default function AdminSupportPanel() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | open | replied | closed
  const [active, setActive] = useState(null); // selected ticket
  const [messages, setMessages] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [reply, setReply] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await supportApi.list(statusFilter !== 'all' ? statusFilter : undefined);
      setTickets(res.data?.tickets || []);
    } catch (e) {
      toast.error('Failed to load support tickets');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { refresh(); }, [refresh]);

  const openThread = async (t) => {
    setActive(t);
    setThreadLoading(true);
    try {
      const res = await supportApi.get(t.id);
      setMessages(res.data?.messages || []);
      setActive(res.data?.ticket || t);
      // Tickets list will reflect zeroed unread count next refresh
      setTickets((prev) => prev.map(x => x.id === t.id ? { ...x, unread_for_admin: 0 } : x));
    } catch (e) {
      toast.error('Failed to load conversation');
    } finally {
      setThreadLoading(false);
    }
  };

  const handleAttach = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error(`Image only. You picked: ${file.type || file.name}`);
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast.error(`Image too large (${(file.size / 1024 / 1024).toFixed(1)} MB, max 4 MB)`);
      return;
    }
    try {
      toast.loading('Uploading…', { id: 'admin-attach' });
      const res = await contentApi.upload(file);
      setAttachment({ url: `/api/content/serve/${res.data.id}`, name: file.name });
      toast.success('Attached', { id: 'admin-attach' });
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Upload failed';
      toast.error(`Attachment failed: ${detail}`, { id: 'admin-attach' });
    }
  };

  const sendReply = async () => {
    if (!reply.trim() || !active || submitting) return;
    setSubmitting(true);
    try {
      const res = await supportApi.reply(active.id, {
        message: reply.trim(),
        attachment_url: attachment?.url || null,
      });
      setMessages((prev) => [...prev, res.data?.message]);
      setReply('');
      setAttachment(null);
      if (res.data?.status) {
        setActive((t) => t ? { ...t, status: res.data.status } : t);
        setTickets((prev) => prev.map(x => x.id === active.id ? { ...x, status: res.data.status, last_message_at: new Date().toISOString() } : x));
      }
    } catch (e) {
      toast.error('Failed to send');
    } finally {
      setSubmitting(false);
    }
  };

  const setStatus = async (status) => {
    if (!active) return;
    try {
      await supportApi.setStatus(active.id, status);
      setActive((t) => t ? { ...t, status } : t);
      setTickets((prev) => prev.map(x => x.id === active.id ? { ...x, status } : x));
      toast.success(`Marked as ${status}`);
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  const filtered = tickets.filter(t => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      (t.subject || '').toLowerCase().includes(s) ||
      (t.user_email || '').toLowerCase().includes(s) ||
      (t.user_name || '').toLowerCase().includes(s)
    );
  });

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
      {!active ? (
        <>
          {/* Filters */}
          <div className="p-3 border-b border-zinc-800 flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by subject, user name or email…"
                className="pl-10 bg-zinc-800 border-zinc-700 text-white text-sm"
                data-testid="admin-support-search"
              />
            </div>
            <div className="flex items-center gap-1 text-xs">
              <Filter className="w-3.5 h-3.5 text-zinc-500" />
              {['all', 'open', 'replied', 'closed'].map(s => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`px-2.5 py-1 rounded-full border transition-colors capitalize ${
                    statusFilter === s
                      ? 'bg-cyan-600/30 border-cyan-500/50 text-cyan-100'
                      : 'bg-zinc-800 border-zinc-700 text-zinc-400 hover:text-zinc-200'
                  }`}
                  data-testid={`admin-support-filter-${s}`}
                >{s}</button>
              ))}
            </div>
          </div>

          {/* List */}
          <div className="max-h-[60vh] overflow-y-auto">
            {loading ? (
              <div className="text-center py-12 text-zinc-500 text-sm">
                <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading…
              </div>
            ) : !filtered.length ? (
              <div className="text-center py-16 text-zinc-500">
                <MessageCircle className="w-10 h-10 mx-auto mb-2 text-zinc-700" />
                <div className="text-sm">No support tickets {statusFilter !== 'all' && `(${statusFilter})`}.</div>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-left">
                  <tr className="border-b border-zinc-800 text-zinc-500 text-xs uppercase tracking-wide">
                    <th className="p-3">Subject</th>
                    <th className="p-3 hidden sm:table-cell">User</th>
                    <th className="p-3">Status</th>
                    <th className="p-3 text-right hidden sm:table-cell">Last Activity</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((t) => (
                    <tr
                      key={t.id}
                      onClick={() => openThread(t)}
                      className="border-b border-zinc-800/50 hover:bg-zinc-800/40 cursor-pointer"
                      data-testid={`admin-ticket-row-${t.id}`}
                    >
                      <td className="p-3">
                        <div className="text-zinc-100 font-medium truncate max-w-[200px] sm:max-w-[300px] flex items-center gap-2">
                          {t.subject}
                          {(t.unread_for_admin || 0) > 0 && (
                            <span className="w-2 h-2 rounded-full bg-fuchsia-400 animate-pulse" />
                          )}
                        </div>
                        <div className="text-[11px] text-zinc-500 mt-0.5">
                          {t.message_count || 1} msg · #{(t.id || '').slice(0, 8)}
                        </div>
                        {/* On mobile, surface user + timestamp inline since
                            their columns are hidden. */}
                        <div className="block sm:hidden text-[11px] text-zinc-500 mt-1">
                          <span className="text-zinc-300">{t.user_name || t.user_email}</span>
                          <span className="mx-1.5">·</span>
                          {new Date(t.last_message_at || t.created_at).toLocaleString()}
                        </div>
                      </td>
                      <td className="p-3 text-zinc-300 hidden sm:table-cell">
                        <div className="text-xs">{t.user_name || '—'}</div>
                        <div className="text-[11px] text-zinc-500">{t.user_email}</div>
                      </td>
                      <td className="p-3"><StatusPill status={t.status} /></td>
                      <td className="p-3 text-right text-[11px] text-zinc-400 hidden sm:table-cell">
                        {new Date(t.last_message_at || t.created_at).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : (
        <div className="flex flex-col h-[70vh]">
          {/* Thread header */}
          <div className="flex items-center justify-between gap-3 p-3 border-b border-zinc-800">
            <div className="flex items-center gap-2 min-w-0">
              <button
                onClick={() => { setActive(null); setMessages([]); refresh(); }}
                className="text-zinc-400 hover:text-white p-1 rounded"
                data-testid="admin-support-back"
              ><ArrowLeft className="w-4 h-4" /></button>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-zinc-100 truncate">{active.subject}</div>
                <div className="text-[11px] text-zinc-500 truncate">
                  {active.user_name || active.user_email} · opened {new Date(active.created_at).toLocaleString()}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <StatusPill status={active.status} />
              {active.status !== 'closed' ? (
                <Button size="sm" variant="outline" onClick={() => setStatus('closed')} className="h-7 text-xs" data-testid="admin-support-close-btn">
                  <Lock className="w-3 h-3 mr-1" /> Close
                </Button>
              ) : (
                <Button size="sm" variant="outline" onClick={() => setStatus('open')} className="h-7 text-xs" data-testid="admin-support-reopen-btn">
                  Re-open
                </Button>
              )}
            </div>
          </div>

          {/* Thread body */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-zinc-950/40">
            {threadLoading ? (
              <div className="text-center text-zinc-500 text-sm py-10">
                <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading…
              </div>
            ) : messages.map((m) => (
              <div key={m.id} className={`flex ${m.sender_role === 'admin' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm ${
                  m.sender_role === 'admin'
                    ? 'bg-cyan-500/10 border border-cyan-500/30 text-zinc-100 rounded-br-sm'
                    : 'bg-fuchsia-500/10 border border-fuchsia-500/30 text-zinc-100 rounded-bl-sm'
                }`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] uppercase tracking-wide font-semibold ${m.sender_role === 'admin' ? 'text-cyan-300' : 'text-fuchsia-300'}`}>
                      {m.sender_role === 'admin' ? 'Support' : (m.sender_name || 'User')}
                    </span>
                    <span className="text-[10px] text-zinc-500">{new Date(m.created_at).toLocaleString()}</span>
                  </div>
                  <div className="whitespace-pre-wrap break-words leading-relaxed">{m.message}</div>
                  {m.attachment_url && (
                    <a href={m.attachment_url} target="_blank" rel="noreferrer" className="mt-2 block rounded-lg overflow-hidden border border-zinc-700 hover:border-cyan-400/60">
                      <img src={m.attachment_url} alt="Attachment" className="max-h-48 w-full object-contain bg-black" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Reply bar */}
          {active.status !== 'closed' ? (
            <div className="border-t border-zinc-800 p-3 bg-zinc-900/80">
              {attachment && (
                <div className="flex items-center gap-2 mb-2 px-2 py-1.5 rounded bg-zinc-800 text-xs">
                  <ImageIcon className="w-3.5 h-3.5 text-cyan-400" />
                  <span className="text-zinc-300 truncate flex-1">{attachment.name}</span>
                  <button onClick={() => setAttachment(null)} className="text-zinc-500 hover:text-zinc-200">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
              <div className="flex gap-2">
                <label className="flex items-center justify-center w-9 h-9 rounded-lg bg-zinc-800 hover:bg-zinc-700 cursor-pointer text-zinc-300 hover:text-cyan-300 shrink-0 border border-zinc-700" title="Attach screenshot">
                  <Paperclip className="w-4 h-4" />
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => { handleAttach(e.target.files?.[0]); e.target.value = ''; }} />
                </label>
                <Textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Reply to the user…"
                  rows={2}
                  className="flex-1 resize-none bg-zinc-800 border-zinc-700 text-sm text-zinc-100 min-h-[36px]"
                  data-testid="admin-support-reply-input"
                />
                <Button
                  onClick={sendReply}
                  disabled={!reply.trim() || submitting}
                  className="bg-cyan-600 hover:bg-cyan-700 text-white h-9 px-3 shrink-0"
                  data-testid="admin-support-reply-send"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          ) : (
            <div className="border-t border-zinc-800 px-4 py-3 text-xs text-zinc-500 flex items-center gap-2 bg-zinc-900/80">
              <Lock className="w-3.5 h-3.5" /> Ticket is closed.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const StatusPill = ({ status }) => {
  const map = {
    open: { label: 'Open', cls: 'bg-amber-500/10 border-amber-500/30 text-amber-300', icon: CircleDashed },
    replied: { label: 'Replied', cls: 'bg-cyan-500/10 border-cyan-500/30 text-cyan-300', icon: CheckCircle2 },
    closed: { label: 'Closed', cls: 'bg-zinc-700/40 border-zinc-700 text-zinc-400', icon: Lock },
  };
  const m = map[status] || map.open;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wider ${m.cls}`}>
      <Icon className="w-3 h-3" /> {m.label}
    </span>
  );
};
